/**
 * Pequeno indicador do estado da caução ("caução paga: 250 € / 500 €").
 *
 * Usado dentro do gate da Direção Técnica (DTReviewLinkCard + CarePlan
 * blocks editor) e no portal público da DT — sempre que o utilizador
 * precisa de saber quanto falta para desbloquear a validação clínica.
 *
 * Aceita **ou** o `deposit` inteiro do lead (`{plan, payments}`) **ou**
 * os pares `paid` + `total` já pré-computados (usado no portal público
 * onde só chegam os totais, não o array de pagamentos).
 */
import React from "react";

const eur = (n) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function DepositProgress({ deposit, paid, total, compact = false, testid = "deposit-progress" }) {
  let paidAmt = typeof paid === "number" ? paid : 0;
  let totalAmt = typeof total === "number" ? total : 0;
  if (deposit && typeof paidAmt !== "number") paidAmt = 0;
  if (deposit) {
    const payments = Array.isArray(deposit.payments) ? deposit.payments : [];
    paidAmt = payments.reduce((s, p) => s + (Number(p?.amount) || 0), 0);
    totalAmt = Number(deposit?.plan?.total_amount) || 0;
  }
  const pct = totalAmt > 0 ? Math.min(100, Math.round((paidAmt / totalAmt) * 100)) : 0;
  const done = totalAmt > 0 && paidAmt + 0.005 >= totalAmt;
  const remaining = Math.max(0, totalAmt - paidAmt);

  return (
    <div
      className={`rounded-lg border ${done ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50/60"} px-3 ${compact ? "py-1.5" : "py-2"} space-y-1`}
      data-testid={testid}
    >
      <div className={`flex items-baseline justify-between gap-3 ${compact ? "text-[11px]" : "text-xs"}`}>
        <span className={`font-semibold ${done ? "text-emerald-900" : "text-amber-900"}`}>
          Caução paga
        </span>
        <span className={`tabular-nums ${done ? "text-emerald-900" : "text-amber-900"}`}>
          <b>{eur(paidAmt)}</b>
          <span className="opacity-60"> / {eur(totalAmt)}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/70 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-amber-500"}`}
          style={{ width: `${pct}%` }}
          data-testid={`${testid}-bar`}
        />
      </div>
      {!done && totalAmt > 0 && (
        <div className={`${compact ? "text-[10px]" : "text-[11px]"} text-amber-800/80`}>
          Falta <b>{eur(remaining)}</b> ({100 - pct}%) para desbloquear a validação da DT.
        </div>
      )}
    </div>
  );
}
